import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api`,
  timeout: 30000,
});

// Attach JWT to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('mn_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const stocksAPI = {
  search: (q) => api.get(`/stocks/search?q=${q}`),
  quote: (symbol) => api.get(`/stocks/quote/${symbol}`),
  history: (symbol, period1, period2, interval = '1d') => api.get(`/stocks/history/${symbol}?period1=${period1}&period2=${period2}&interval=${interval}`),
  nifty50: () => api.get('/stocks/nifty50'),
  sp500: () => api.get('/stocks/sp500'),
  watchlist: (symbols) => api.get(`/stocks/watchlist?symbols=${symbols}`),
};

export const fundamentalsAPI = {
  get: (symbol) => api.get(`/fundamentals/${symbol}`),
  aiAnalysis: (symbol) => api.get(`/fundamentals/${symbol}/ai-analysis`),
  news: (symbol) => api.get(`/fundamentals/${symbol}/news`, { timeout: 60000 }),
};

export const sectorsAPI = {
  all: (market) => api.get(`/sectors?market=${market}`),
  fundamentals: (sector, market) => api.get(`/sectors/${sector}/fundamentals?market=${market}`),
  seasonality: () => api.get('/sectors/monthly/seasonality'),
  aiSeasonality: (month) => api.get(`/sectors/monthly/ai-analysis${month ? `?month=${encodeURIComponent(month)}` : ''}`),
};

export const aiAPI = {
  query: (prompt, context) => api.post('/ai/query', { prompt, context }),
  marketSummary: () => api.get('/ai/market-summary'),
  bestSectors: () => api.get('/ai/best-sectors-now'),
  usage: () => api.get('/ai/usage'),
};

export const trendingAPI = {
  today: () => api.get('/trending/today', { timeout: 90000 }),
  // Same 90s budget as /today — this now runs a full Tavily + Gemini
  // regenerate synchronously, not a fast cache-clear.
  refresh: () => api.delete('/trending/cache', { timeout: 90000 }),
};

export const macroAPI = {
  overview: () => api.get('/macro/overview', { timeout: 60000 }),
  positioning: () => api.get('/macro/positioning', { timeout: 60000 }),
  risks: () => api.get('/macro/risks', { timeout: 60000 }),
  centralBanks: () => api.get('/macro/central-banks', { timeout: 60000 }),
};

export const quantAPI = {
  screener:        (market = 'india') => api.get(`/quant/screener?market=${market}`),
  sectorRanking:   (market = 'india') => api.get(`/quant/sector-ranking?market=${market}`),
  technicalSignals:(market = 'india') => api.get(`/quant/technical-signals?market=${market}`),
};

export const strategyAPI = {
  screener: (market = 'india') => api.get(`/strategy/screener?market=${market}`),
};

export const watchlistAPI = {
  get:    ()             => api.get('/watchlist'),
  add:    (symbol, name) => api.post('/watchlist', { symbol, name }),
  remove: (symbol)       => api.delete(`/watchlist/${symbol}`),
};

export const actionAPI = {
  get:   (symbol) => api.get(`/action/${symbol}`, { timeout: 45000 }),
  usage: ()       => api.get('/action/usage'),
};

export const portfolioAPI = {
  analyze: (symbols) => api.post('/portfolio/analyze', { symbols }, { timeout: 90000 }),
  history: ()        => api.get('/portfolio/history'),
};

export const fpiAPI = {
  available: ()         => api.get('/fpi/available'),
  latest:    ()         => api.get('/fpi/latest'),
  report:    (reportId) => api.get(`/fpi/${reportId}`),
  trends:    ()         => api.get('/fpi/trends'), // sector-level 3M/6M net-flow + trend direction
};

export const playbookAPI = {
  get: () => api.get('/playbook', { timeout: 60000 }),
  getSectorCompanies: (sector) => api.get(`/playbook/sector/${sector}/companies`, { timeout: 30000 }),
};

export const mentorshipAPI = {
  modules: () => api.get('/mentorship/modules'),
  // streamUrl returns the direct URL string (not an axios call) — passed to hls.js
  streamUrl: (moduleId, lessonId) =>
    `${process.env.REACT_APP_API_URL}/api/mentorship/stream/${moduleId}/${lessonId}/index.m3u8`,
};

// Stock Research page Engines — see STOCK_RESEARCH_ARCHITECTURE.md.
// Business Overview has no entry here — it's a zero-backend module that
// reuses fundamentalsAPI.get() directly.
export const researchLabAPI = {
  // observationMode was silently dropped here before (destructured out,
  // never forwarded to `params`) — the frontend's episodes/every toggle
  // (data-audit gap P0.3) had no effect on the actual request. Fixed by
  // passing the whole params object through instead of re-listing fields
  // that drift from routes/researchLab.js's real query params over time.
  valuationRegime: (params) =>
    api.get(`/research-lab/valuation-regime`, { params, timeout: 30000 }),
  sectorFlowRegime: (params) =>
    api.get(`/research-lab/sector-flow-regime`, { params, timeout: 30000 }),
  // Free/Pro (Phase 3): the curated sample-study list — Free users can run
  // any of these; anything else requires Pro (see routes/researchLab.js).
  presets: () => api.get('/research-lab/presets'),
  // Free/Pro (Phase 3, spec #5): Multi-Factor Research — Pro only, no free
  // sample (unlike the two studies above).
  multiFactor: (params) =>
    api.get('/research-lab/multi-factor', { params, timeout: 30000 }),
  // Macro Regime Study (data-audit gap P1.2) — built on macroHistory.js's
  // existing live FRED/Yahoo fetch, no new data source.
  macroRegime: (params) =>
    api.get('/research-lab/macro-regime', { params, timeout: 30000 }),
  // Condition Builder (spec Phase 3, Step 6) — POST + JSON body, not GET +
  // query params: `conditions` is a variable-length array of typed objects,
  // which doesn't serialize predictably as a flat query string the way
  // every other Research Lab call's scalar params do.
  conditionBuilder: (body) =>
    api.post('/research-lab/condition-builder', body, { timeout: 30000 }),
};

export const researchAPI = {
  companyIntelligence:  (symbol)          => api.get(`/research/${symbol}/company-intelligence`, { timeout: 45000 }),
  compareCompanyIntelligence: (symbols)   => api.get(`/research/compare/company-intelligence?symbols=${symbols.join(',')}`, { timeout: 60000 }),
  financialTrends:      (symbol)          => api.get(`/research/${symbol}/financial-trends`, { timeout: 30000 }),
  compareFinancialTrends: (symbols)       => api.get(`/research/compare/financial-trends?symbols=${symbols.join(',')}`, { timeout: 30000 }),
  capex:                (symbol)          => api.get(`/research/${symbol}/capex`, { timeout: 30000 }),
  compareCapex:          (symbols)       => api.get(`/research/compare/capex?symbols=${symbols.join(',')}`, { timeout: 30000 }),
  returnAttribution:      (symbol)        => api.get(`/research/${symbol}/return-attribution`, { timeout: 30000 }),
  compareReturnAttribution: (symbols)     => api.get(`/research/compare/return-attribution?symbols=${symbols.join(',')}`, { timeout: 30000 }),
  businessQuality:      (symbol)          => api.get(`/research/${symbol}/business-quality`, { timeout: 30000 }),
  compareBusinessQuality: (symbols)       => api.get(`/research/compare/business-quality?symbols=${symbols.join(',')}`, { timeout: 30000 }),
  valuation:            (symbol)          => api.get(`/research/${symbol}/valuation`, { timeout: 30000 }),
  compareValuation:     (symbols)         => api.get(`/research/compare/valuation?symbols=${symbols.join(',')}`, { timeout: 30000 }),
  technical:            (symbol)          => api.get(`/research/${symbol}/technical`, { timeout: 45000 }),
  compareTechnical:     (symbols)         => api.get(`/research/compare/technical?symbols=${symbols.join(',')}`, { timeout: 45000 }),
  ownership:            (symbol)          => api.get(`/research/${symbol}/ownership`, { timeout: 30000 }),
  compareOwnership:     (symbols)         => api.get(`/research/compare/ownership?symbols=${symbols.join(',')}`, { timeout: 30000 }),
  sectorForSymbol:      (symbol)          => api.get(`/research/${symbol}/sector`, { timeout: 30000 }),
  sector:               (sectorKey)       => api.get(`/research/sector/${sectorKey}`, { timeout: 30000 }),
  compareSector:        (sectorKeys)      => api.get(`/research/compare/sector?sectors=${sectorKeys.join(',')}`, { timeout: 30000 }),
  globalMacro:          ()                => api.get(`/research/macro/global`, { timeout: 30000 }),
  globalMacroHistory:   ()                => api.get(`/research/macro/global/history`, { timeout: 30000 }),
  globalMacroImpact:    (symbol)          => api.get(`/research/${symbol}/global-macro-impact`, { timeout: 30000 }),
  indiaMacro:           ()                => api.get(`/research/macro/india`, { timeout: 45000 }),
  indiaMacroHistory:    ()                => api.get(`/research/macro/india/history`, { timeout: 30000 }),
  historicalEvents:     (symbol)          => api.get(`/research/${symbol}/historical-events`, { timeout: 60000 }),
  aiSummary:            (symbol)          => api.get(`/research/${symbol}/ai-summary`, { timeout: 60000 }),
  sectorCycle:          ()                => api.get(`/research/sectors/cycle`, { timeout: 30000 }), // Market Intelligence — all sectors ranked
};

// Free/Pro (Phase 4, spec §16) — "My Research". Pro-only server-side
// (routes/savedResearch.js); this file just exposes the CRUD calls.
export const savedResearchAPI = {
  list:   ()                     => api.get('/saved-research'),
  create: (type, label, config)  => api.post('/saved-research', { type, label, config }),
  remove: (id)                   => api.delete(`/saved-research/${id}`),
};

// Free/Pro (Phase 4, spec §17) — Research Alerts. Pro-only server-side
// (routes/researchAlerts.js). Status reflects the last nightly cron check
// (services/researchAlerts.js) — never computed on request.
// Data pipeline monitoring (admin-only) — backend gates every one of these
// on the X-Admin-Key header (see Stock-AI-Backend/src/routes/admin.js),
// not the JWT the rest of this file attaches, so the key is passed
// per-request instead of via the interceptor above.
function adminHeaders(key) {
  return { headers: { 'X-Admin-Key': key } };
}
export const pipelineAPI = {
  summary:        (key)               => api.get('/admin/pipeline/summary', adminHeaders(key)),
  runs:           (key, limit = 50)   => api.get(`/admin/pipeline/runs?limit=${limit}`, adminHeaders(key)),
  securities:     (key, opts = {})    => api.get(`/admin/pipeline/securities?limit=${opts.limit || 25}&search=${encodeURIComponent(opts.search || '')}`, adminHeaders(key)),
  announcements:  (key, opts = {})    => api.get(`/admin/pipeline/announcements?limit=${opts.limit || 25}&search=${encodeURIComponent(opts.search || '')}`, adminHeaders(key)),
  corporateActions: (key, opts = {})  => api.get(`/admin/pipeline/corporate-actions?limit=${opts.limit || 25}&search=${encodeURIComponent(opts.search || '')}`, adminHeaders(key)),
  resultCalendar: (key, opts = {})    => api.get(`/admin/pipeline/result-calendar?limit=${opts.limit || 25}&search=${encodeURIComponent(opts.search || '')}`, adminHeaders(key)),
  financialResults: (key, opts = {})  => api.get(`/admin/pipeline/financial-results?limit=${opts.limit || 25}&search=${encodeURIComponent(opts.search || '')}`, adminHeaders(key)),
  shareholding:   (key, opts = {})    => api.get(`/admin/pipeline/shareholding?limit=${opts.limit || 25}&search=${encodeURIComponent(opts.search || '')}`, adminHeaders(key)),
};

export const researchAlertsAPI = {
  list:    ()                    => api.get('/research-alerts'),
  create:  (type, label, config) => api.post('/research-alerts', { type, label, config }),
  action:  (id, action)          => api.patch(`/research-alerts/${id}`, { action }), // 'pause' | 'resume' | 'reset'
  remove:  (id)                  => api.delete(`/research-alerts/${id}`),
  types:   ()                    => api.get('/research-alerts/meta/types'),
};
